import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Campaigns } from '@components/templates/marketplace';
import { SITE_URLS } from '@constants';
import { useSEOMetatags } from '@hooks/useSEOMetatags';
import { useGetCampaignBundles } from '@services/useGetCampaignBundles';
import { useData } from '@hooks/useData';
import { useGetCampaigns } from '@services/useGetCampaigns';

export function CampaignsPage() {
    const { t } = useTranslation();
    const { allSettings, campaignBundles, campaigns } = useData();
    const { request: requestCampaignsBundles } = useGetCampaignBundles();
    const { request: requestCampaigns } = useGetCampaigns();

    const translations = {
        title: t('campaigns-component.metatags.title', { marketplace: allSettings?.data?.marketplaceName }),
        description: t('campaigns-component.metatags.description', { marketplace: allSettings?.data?.marketplaceName }),
    };

    const seoImage = campaigns?.data?.[0]?.desktopImg;

    useSEOMetatags({
        title: translations.title,
        description: translations.description,
        image: seoImage?.src
            ? {
                  src: seoImage.src || '',
                  width: seoImage.width,
                  height: seoImage.height,
                  alt: campaigns?.data?.[0]?.title || '',
              }
            : undefined,
        url: SITE_URLS.campaigns,
        index: true,
    });

    useEffect(() => {
        requestCampaignsBundles();
        requestCampaigns();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formattedCampaignBundles = campaignBundles?.data
        ? campaignBundles?.data?.map((bundle) => ({
              uuid: bundle.uuid || '',
              merchants: bundle.merchants || [],
              name: bundle.name || '',
              image: bundle.image?.src || '',
              price: bundle.price || 0,
              oldPrice: bundle.oldPrice,
          }))
        : [];

    const formattedCampaignsForCarousel = (campaigns?.data || []).map((campaign) => {
        return {
            theme: campaign.theme || 'dark',
            desktopImg: campaign.desktopImg,
            mobileImg: campaign.mobileImg,
            category: campaign.category || '',
            title: campaign.title || '',
            subtitle: campaign.subtitle || '',
            description: '',
            link: {
                label: campaign.link?.label || '',
                url: campaign.link?.url || '',
            },
            footerNote: campaign.footerNote || '',
            products: campaign.products || [],
        };
    });

    const allProductsFromAllCampaigns: {
        uuid?: string;
        name?: string;
        merchant?: string;
        image?: string;
        price?: number;
        oldPrice?: number;
        promo?: number;
        favorite?: boolean;
    }[] = [];

    (campaigns?.data || []).forEach((campaign) => {
        if (campaign.products && Array.isArray(campaign.products)) {
            allProductsFromAllCampaigns.push(...campaign.products);
        }
    });

    const uniqueProducts = Array.from(new Map(allProductsFromAllCampaigns.map((product) => [product.uuid, product])).values());
    const shuffledProducts = uniqueProducts.sort(() => 0.5 - Math.random());
    const randomFourProducts = shuffledProducts.slice(0, 4);

    const productsHighlights = randomFourProducts.map((product) => ({
        uuid: product.uuid || '',
        name: product.name || '',
        merchant: product.merchant || '',
        image: product.image || '',
        price: parseFloat(`${product.price || 0}`),
        oldPrice: product.oldPrice ? parseFloat(`${product.oldPrice}`) : 0,
        promo: product.promo ? parseFloat(`${product.promo}`) : 0,
        initialFavorite: product.favorite || false,
    }));

    return (
        <Campaigns
            carouselItems={formattedCampaignsForCarousel}
            productsHighlights={productsHighlights}
            promoBasketsHighlights={formattedCampaignBundles}
        />
    );
}
